import React, { useState, useEffect } from 'react';
import './ScreenSettings.css';
import { Slider, Skeleton, Tooltip } from 'antd';
import socketIoClient from 'socket.io-client';
import { url } from 'utils/url';
import { log } from 'utils';
import { useSelector, useDispatch } from 'react-redux';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import {
  setConnection, setSelectedScreenId, fetchHistory,
  requestScreenshot, handleInDeviceData,
  handleIncomingSystemData, setScreenshot,
} from 'state/actions/screenInformation';
import {
  connectSocket,
} from 'state/actions/settings';
import { ClipLoader } from 'react-spinners';
import { Empty } from 'antd';
import { NotConnectedIcon } from 'assets/icon/bigicon';
import { CameraIcon, TrashIcon } from 'assets/icon/dynamic';

function ScreenInformation({ handleChangeTab }) {
  // const { siteId } = useParams();
  const dispatch = useDispatch();
  const {
    screens = {},
    selectedScreenId = null,
    historyData = [],
    screenshots = {},
    isConnected = false
  } = useSelector(state => state.system || {});

  const { screenLoading, activeScreen } = useSelector((state) => ({
    screenList: state.screens.list,
    screenLoading: state.screens.loading,
    activeScreen: state.screens.activeScreen,
  }));


  const [deviceInfo, setDeviceInfo] = useState(null);
  const [socket, setSocket] = useState(null);

  // const [captureLoading, setCaptureLoading] = useState(false);

  const [error, setError] = useState(false);

  const [loading, setLoading] = useState({
    device: true,
    sound: true,
    picture: true,
  });

  const [screenshoot, setScreenshoot] = useState(null);

  const activeData = activeScreen ? screens[activeScreen] : null;

  const data = {
    name: 'adsign',
    screen: activeScreen._id
  };

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  useEffect(() => {
    const newSocket = socketIoClient(url, {
      query: {
        name: 'adsign',
      },
    });
    setSocket(newSocket);

    // Good practice: disconnect when the component unmounts
    return () => newSocket.disconnect();
  }, [url]);

  useEffect(() => {
    console.log('activeScreen changed:', activeScreen._id);
    console.log('socket changed:', socket);
    if (activeScreen && socket) {
      dispatch(fetchHistory(activeScreen._id));
      connectSocket(activeScreen._id);
    }
  }, [activeScreen, socket, dispatch]);

  useEffect(() => {
    if (screenshots) {
      setScreenshoot(screenshots.image);
    }
  }, [screenshots]);

  useEffect(() => {
    if (historyData) {
      setDeviceInfo(historyData[historyData.length - 1]);
    }
  }, [historyData]);

  useEffect(() => {
    if (!deviceInfo) {
      setError(true);
    }
  }, [deviceInfo]);

  useEffect(() => {
    if (!socket) return;

    socket.on('connect', () => dispatch(setConnection(true)));
    socket.on('disconnect', () => dispatch(setConnection(false)));

    socket.on('system_data', (data) => {
      dispatch(handleIncomingSystemData(data));
    });

    socket.on('in_capture', (payload) => {
      dispatch(setScreenshot(payload.screenId, payload.image));
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('system_data');
      socket.off('screenshot_data');
    };
  }, [socket, dispatch]); // Added socket as a dependency

  let ramUsagePercent = 0;
  let storageUsagePercent = 0;
  let ramColor = '#3b82f6';
  let storageColor = '#10b981';

  if (deviceInfo && deviceInfo.ram && deviceInfo.storage) {
    ramUsagePercent = ((deviceInfo.ram.used / deviceInfo.ram.total) * 100).toFixed(1);
    storageUsagePercent = ((deviceInfo.storage.used / deviceInfo.storage.total) * 100).toFixed(1);
    ramColor = ramUsagePercent > 90 ? '#ef4444' : '#3b82f6';
    storageColor = storageUsagePercent > 90 ? '#ef4444' : '#10b981';
  }

  const formatGB = (bytes) => (bytes / 1024 ** 3).toFixed(2) + ' GB';


  useEffect(() => {
    setDeviceInfo(null);
    if (socket && activeScreen._id) {
      socket.on('connect', () => {
        socket.emit('message', data);
      });
      socket.on('in_device', (data) => {
        log(data, 'data in device');
        if (data.screenId === activeScreen._id) {
          setError(false);
          // setDeviceInfo(data);
          if (data) {
            dispatch(handleInDeviceData(data));
          }
          setLoading({
            ...loading,
            device: false,
          });
        } else {
          setDeviceInfo(null);
          setLoading({
            device: true,
            sound: true,
            picture: true,
          });
        }
      });
    } else {
      log('socket and activeScreen is not called');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, activeScreen._id]);


  const handleTryAgain = () => {
    connectSocket(activeScreen._id);
    setLoading({
      device: true,
      sound: true,
      picture: true,
    });
    setError(false);
    getDeviceInfo();
  };

  const getDeviceInfo = async () => {
    log('get device info');
    socket.emit('message', data, (response) => {
      log(response, 'response socket'); // ok
    });
    await delay();
    if (!deviceInfo) {
      setError(true);
    }
  };

  const handleCapture = () => {
    dispatch(requestScreenshot(activeScreen._id));
  };

  const ProgressBar = ({ title, percentage, total, used, free, color }) => (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#334155' }}>{title}</h4>
        <span style={{ fontWeight: 'bold', color: color }}>{percentage}%</span>
      </div>
      <div style={{ height: '20px', backgroundColor: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ width: `${percentage}%`, backgroundColor: color, height: '100%', transition: 'width 1s ease-in-out' }}></div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '0.9rem', color: '#64748b' }}>
        <span><strong>Used:</strong> {used}</span>
        <span><strong>Total:</strong> {total}</span>
        <span><strong>Free:</strong> {free}</span>
      </div>
    </div>
  );

  return (
    <div className="screen p-20">
      {screenLoading ? (
        <ClipLoader />
      ) : (
        <div>
          <div>
            {log(error, 'error')}
            {error ? (
              <div className="screen-not-connected">
                <Empty image={<NotConnectedIcon />} description="" />
                <h1>The device isn't connected</h1>
                <button
                  className="btn-outline-primary"
                  onClick={handleTryAgain}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <div className="screen-content">
                  <div className="screen-column">
                    <p className="screen-title">Device Information</p>
                    <div >
                      {console.log(deviceInfo, 'device info')}
                      {loading.device && !deviceInfo ? (
                        <Skeleton loading />
                      ) : deviceInfo && deviceInfo.ram && deviceInfo.storage && (
                        <>
                          <div>
                            <ProgressBar
                              title="RAM Usage" percentage={ramUsagePercent} total={formatGB(deviceInfo.ram?.total)} used={formatGB(deviceInfo.ram?.used)} free={formatGB(deviceInfo.ram?.free)} color={ramColor}
                            />

                          </div>
                          <div>
                            <ProgressBar
                              title="Storage Usage" percentage={storageUsagePercent} total={formatGB(deviceInfo.storage?.total)} used={formatGB(deviceInfo.storage?.used)} free={formatGB(deviceInfo.storage?.free)} color={storageColor}
                            />
                          </div>

                          <div>
                            <div><strong>Hostname:</strong> {deviceInfo.agent_hostname}</div>
                          </div>
                          <div>
                            <div><strong>Antivirus:</strong> {deviceInfo.antivirus}</div>
                          </div>
                          <div>
                            <div><strong>OS:</strong> {deviceInfo.os.platform} ({deviceInfo.os.release})</div>
                          </div>
                          <div>
                            <div><strong>CPU:</strong> {deviceInfo.cpu.model}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="screen-column">
                    <div className="screen-column">
                      <p className="screen-title">Take a screenshot</p>
                      {(
                        <>
                          <div className="screen-shoot">
                            {screenshoot ? (
                              <div>
                                <img src={screenshoot} alt="Screenshot" />
                                <Tooltip title="Delete">
                                  <button
                                    className="button-remove"
                                    onClick={() => setScreenshoot(null)}
                                  >
                                    <TrashIcon color="#CB3A31" size={20} />
                                  </button>
                                </Tooltip>
                              </div>
                            ) : (
                              <button
                                className="camera-button"
                                onClick={handleCapture}
                              >
                                <CameraIcon size={18} color="#64D5D2" />
                                Capture
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ScreenInformation;
